import { NavLink, useLocation } from "react-router-dom";
import { LogOut, MoreVertical, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { useLogout } from "@/features/auth/useLogout";
import { cn } from "@/lib/utils";

/**
 * The header's overflow menu: theme, Settings, and Sign out behind one control.
 *
 * This is the phone form only. From 640px up the same three controls keep their
 * own places on the bar and this trigger is hidden — see the
 * `.page-header-inline-actions` / `.page-header-overflow` pair in index.css,
 * which is the single place that switch is made. Below 640px the three cost
 * ~145px next to a title and an account block and wrapped the header onto extra
 * rows, so there they collapse behind one control.
 *
 * The theme control is the real switch rather than a menu item that toggles: it
 * shows which mode is active at a glance, which a one-line item cannot. It sits
 * in a plain row, not a `DropdownMenuItem`, so the switch keeps its own
 * `role="switch"` semantics instead of being wrapped in a `menuitem`.
 */
export function HeaderActionsMenu({ className = "" }) {
  const location = useLocation();
  const { logout, isLoggingOut } = useLogout();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="More actions"
            className={cn("touch-target-icon text-muted-foreground", className)}
          />
        }
      >
        <MoreVertical className="size-4" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56 p-1">
        <div className="header-menu-row">
          <span>Theme</span>
          <ThemeToggle />
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          render={<NavLink to="/settings" />}
          data-active={location.pathname === "/settings" ? "true" : undefined}
        >
          <Settings className="size-4" aria-hidden="true" />
          Settings
        </DropdownMenuItem>

        <DropdownMenuItem disabled={isLoggingOut} onClick={logout}>
          <LogOut className="size-4" aria-hidden="true" />
          {isLoggingOut ? "Signing out…" : "Sign out"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
